import { censusLoginPrecheck } from "@/api/census-account-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { Mail } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2Input,
  V2Typography,
} from "@cakewalk/ui/v2";
import { type FormEvent, useState } from "react";

/**
 * Email-first entry ("First time?") for company-census.
 *
 * The user enters just their email; we ask the public precheck which of three
 * doors they belong to and hand the decision back to the orchestrator:
 *   - `hasLogin`               → they already have a login → go to sign-in;
 *   - `existsInDb && !hasLogin`→ known contact, no login yet → activation;
 *   - `!existsInDb`            → brand-new → prospect self-signup.
 *
 * The precheck returns only booleans (never PII), matching the login step.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CompanyCensusGetStartedStep({
  defaultEmail,
  playBuild = false,
  ceremony = null,
  onHasLogin,
  onNeedsActivation,
  onNewProspect,
  onBackToLogin,
}: {
  defaultEmail?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onHasLogin: (email: string) => void;
  onNeedsActivation: (email: string) => void;
  onNewProspect: (email: string) => void;
  onBackToLogin: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      const { hasLogin, existsInDb } = await censusLoginPrecheck(trimmed);
      if (hasLogin) {
        onHasLogin(trimmed);
        return;
      }
      if (existsInDb) {
        onNeedsActivation(trimmed);
        return;
      }
      onNewProspect(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div key="get-started" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              First time?
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Let&rsquo;s get started
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              Enter your work email and we&rsquo;ll take you to the right next step — logging in,
              activating your account, or setting up a new one.
            </V2Typography>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            data-track="form-company-census-get-started"
          >
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <V2FormField label="Email" error={error}>
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || email.trim().length === 0}
                data-track="button-company-census-get-started-continue"
              >
                {isLoading ? "Checking…" : "Continue"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
            <V2Typography variant="meta" muted>
              Already have an account?
            </V2Typography>
            <V2Button
              variant="link"
              onClick={onBackToLogin}
              data-track="button-company-census-get-started-back-to-login"
            >
              Back to login
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>
    </div>
  );
}
